import { FilterOption } from './ColumnFilter';

/** Values each column filter has unticked, keyed by filter section. */
export type HiddenFilters = Record<string, string[]>;

/** Placeholder value for rows with nothing in the column, so they can be filtered too. */
export const NONE = '(none)';

/**
 * Options for one filter section: every value present in `rows` with its count,
 * plus any value currently hidden, so an unticked value never disappears from
 * the list it has to be re-ticked in.
 */
export function tallyOptions<T>(
  rows: T[],
  pick: (row: T) => string | string[] | null | undefined,
  options: {
    hidden?: string[];
    label?: (value: string) => string;
    /** Label for rows with nothing in the column. */
    noneLabel?: string;
    title?: (value: string) => string | undefined;
    order?: (left: string, right: string) => number;
  } = {}
): FilterOption[] {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const picked = pick(row);
    const values = Array.isArray(picked) ? picked : [picked ?? NONE];
    new Set(values.length ? values : [NONE]).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
  });
  (options.hidden || []).forEach((value) => {
    if (!counts.has(value)) counts.set(value, 0);
  });
  const label = options.label ?? ((value: string) => value);
  const order = options.order ?? ((left: string, right: string) => {
    if (left === NONE) return 1;
    if (right === NONE) return -1;
    return label(left).localeCompare(label(right));
  });
  return Array.from(counts.keys()).sort(order).map((value) => ({
    value,
    label: value === NONE ? options.noneLabel ?? '(blank)' : label(value),
    count: counts.get(value) || 0,
    title: options.title?.(value)
  }));
}

/** Whether a single-valued cell survives its section's hidden list. */
export const shown = (hidden: string[] | undefined, value: string | null | undefined): boolean =>
  !hidden || hidden.length === 0 || !hidden.includes(value ?? NONE);

/** A short description of what a section is showing, for the active-filter chips. */
export function describeHidden(hidden: string[], options: FilterOption[]): string {
  const labelOf = (value: string) => options.find((option) => option.value === value)?.label ?? value;
  const visible = options.filter((option) => !hidden.includes(option.value));
  if (visible.length === 0) return 'nothing';
  if (visible.length <= 2) return `only ${visible.map((option) => option.label).join(', ')}`;
  if (hidden.length <= 2) return `not ${hidden.map(labelOf).join(', ')}`;
  return `${visible.length} of ${options.length}`;
}
